import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ShoppingBag, Minus, Plus, Trash2, ExternalLink, Loader2, Info } from "lucide-react";
import { useCartStore } from "@/lib/cartStore";
import { formatPrice } from "@/lib/shopify";

export function CartDrawer() {
  const [open, setOpen] = useState(false);
  const [returnAck, setReturnAck] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const {
    items, isLoading, isSyncing, updateQuantity, removeItem,
    getCheckoutUrl, syncCart, requestInsuranceInvoice, setRequestInsuranceInvoice,
  } = useCartStore();
  const visibleItems = hydrated ? items : [];
  const visibleInvoiceRequest = hydrated ? requestInsuranceInvoice : false;
  const totalItems = visibleItems.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = visibleItems.reduce((s, i) => s + parseFloat(i.price.amount) * i.quantity, 0);
  const currency = visibleItems[0]?.price.currencyCode ?? "USD";

  useEffect(() => { setHydrated(true); }, []);
  useEffect(() => { if (open) syncCart(); }, [open, syncCart]);

  const checkout = () => {
    if (!returnAck) return;
    const url = getCheckoutUrl();
    if (url) { window.open(url, "_blank"); setOpen(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={`Open cart${totalItems > 0 ? `, ${totalItems} item${totalItems > 1 ? "s" : ""}` : ""}`}>
          <ShoppingBag className="h-5 w-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 min-w-5 rounded-full bg-primary p-0 px-1 text-[10px] font-medium text-primary-foreground">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Your bag</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Your bag is empty." : `${totalItems} item${totalItems > 1 ? "s" : ""}`}
          </SheetDescription>
        </SheetHeader>

        {visibleItems.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center text-muted-foreground">
              <ShoppingBag className="mx-auto mb-4 h-10 w-10 opacity-40" />
              <p>Your bag is empty.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <ul className="divide-y divide-border">
                {visibleItems.map((item) => (
                  <li key={item.variantId} className="flex gap-4 py-4">
                    <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded bg-muted">
                      {item.product.node.images?.edges?.[0]?.node && (
                        <img
                          src={item.product.node.images.edges[0].node.url}
                          alt={item.product.node.title}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base leading-tight">{item.product.node.title}</p>
                      <p className="text-xs text-muted-foreground">{item.selectedOptions.map((o) => o.value).join(" · ")}</p>
                      <p className="mt-1 text-sm">{formatPrice(item.price.amount, item.price.currencyCode)}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <Button variant="outline" size="icon" className="h-7 w-7" aria-label="Decrease quantity" onClick={() => updateQuantity(item.variantId, item.quantity - 1)}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <Button variant="outline" size="icon" className="h-7 w-7" aria-label="Increase quantity" onClick={() => updateQuantity(item.variantId, item.quantity + 1)}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Remove ${item.product.node.title} from cart`} onClick={() => removeItem(item.variantId)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>

                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4 border-t border-border pt-4">
              <label className="flex items-start gap-3 rounded border border-border/70 bg-secondary/40 p-3 text-sm">
                <Checkbox
                  checked={visibleInvoiceRequest}
                  onCheckedChange={(v) => setRequestInsuranceInvoice(v === true)}
                  className="mt-0.5"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5 font-medium">
                    Request a cranial prosthesis invoice
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild><Info className="h-3.5 w-3.5 text-muted-foreground" /></TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs">
                          Insurers often require the item billed as a cranial prosthesis (not a wig), with our tax ID
                          and HCPCS code A9282, to approve reimbursement. Confirm coverage with your insurer.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    For insurance or HSA reimbursement for medical hair loss.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 rounded border border-bronze/40 bg-bronze/5 p-3 text-sm">
                <Checkbox
                  checked={returnAck}
                  onCheckedChange={(v) => setReturnAck(v === true)}
                  className="mt-0.5"
                  aria-required
                />
                <span className="flex-1 text-xs leading-relaxed">
                  I have read and agree to the{" "}
                  <a href="/legal/returns" target="_blank" rel="noopener" className="font-medium text-bronze underline">
                    Return Policy
                  </a>{" "}
                  — 7-day window, 20% restocking fee, no returns on wigs that have been altered, cut, worn with fragrance, exposed to smoke, torn, or carry any odor.
                </span>
              </label>

              <p className="text-xs text-muted-foreground">
                Ships in discreet, unbranded packaging.
              </p>

              <div className="flex items-center justify-between">
                <span className="eyebrow">Subtotal</span>
                <span className="font-display text-xl">{formatPrice(totalPrice, currency)}</span>
              </div>
              <Button onClick={checkout} className="w-full" size="lg" disabled={isLoading || isSyncing || !returnAck}>
                {isLoading || isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><ExternalLink className="mr-2 h-4 w-4" /> {returnAck ? "Continue to checkout" : "Accept return policy to continue"}</>
                )}
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">Secure checkout by Shopify. Taxes & shipping calculated at next step.</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
